/**
 * Best-effort Web Storage wrapper (#5873).
 *
 * Browsers in private / strict-privacy modes and in in-app webviews (Facebook,
 * Instagram) can block Web Storage. In those contexts the `window.localStorage`
 * / `window.sessionStorage` GETTER ITSELF throws `SecurityError` — merely
 * touching the property crashes the script. Because our leadgen bundles read
 * `window.localStorage` at module-evaluation time (the vuex-persist config in
 * signup/shared/persistence.js), that crash happened before Vue mounted and the
 * v4 signup page rendered blank.
 *
 * This wrapper feature-detects each backend exactly once, behind try/catch, and
 * silently falls back to an in-memory Map for the lifetime of the page. Call
 * sites import `safeLocalStorage` / `safeSessionStorage` instead of touching
 * `window.*Storage` directly, so nothing downstream ever throws. Persistence
 * becomes best-effort: it survives reloads when storage works and degrades to
 * in-memory (session-only) when it does not.
 *
 * The returned objects expose the subset of the Storage interface we use
 * (getItem/setItem/removeItem/clear/key/length) and are shaped so they can be
 * handed straight to vuex-persist 3.x as its `storage` option.
 */

function createMemoryStorage() {
    const map = new Map();
    return {
        getItem(key) {
            return map.has(key) ? map.get(key) : null;
        },
        setItem(key, value) {
            map.set(key, String(value));
        },
        removeItem(key) {
            map.delete(key);
        },
        clear() {
            map.clear();
        },
        key(index) {
            return Array.from(map.keys())[index] ?? null;
        },
        get length() {
            return map.size;
        },
    };
}

/**
 * Return a native Storage only if it is both reachable AND writable, else null.
 *
 * The property read is inside the try because the getter can throw. A probe
 * write+remove then catches browsers (e.g. Safari private mode) that expose the
 * object but throw on the first `setItem`, so we can fall back before any real
 * call site hits the failure.
 */
function resolveNativeStorage(type) {
    try {
        const storage = window[type];
        if (!storage) {
            return null;
        }
        const probe = '__safe_storage_probe__';
        storage.setItem(probe, '1');
        storage.removeItem(probe);
        return storage;
    } catch (e) {
        return null;
    }
}

/**
 * Wrap a backend so individual operations never throw. Detection already picked
 * a usable backend, but native storage can still throw at runtime (quota); reads
 * degrade to null and writes are dropped rather than crashing the flow.
 */
function makeSafeStorage(type) {
    const backend = resolveNativeStorage(type) || createMemoryStorage();
    return {
        getItem(key) {
            try {
                return backend.getItem(key);
            } catch (e) {
                return null;
            }
        },
        setItem(key, value) {
            try {
                backend.setItem(key, value);
            } catch (e) {
                /* best-effort: storage unavailable or full */
            }
        },
        removeItem(key) {
            try {
                backend.removeItem(key);
            } catch (e) {
                /* best-effort */
            }
        },
        clear() {
            try {
                backend.clear();
            } catch (e) {
                /* best-effort */
            }
        },
        key(index) {
            try {
                return backend.key(index);
            } catch (e) {
                return null;
            }
        },
        get length() {
            try {
                return backend.length;
            } catch (e) {
                return 0;
            }
        },
    };
}

export const safeLocalStorage = makeSafeStorage('localStorage');
export const safeSessionStorage = makeSafeStorage('sessionStorage');
